//React
import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
//Material UI
import CheckCircleIcon from "../../assets/images/CheckCircle.svg";
import WarningCircleIcon from "../../assets/images/WarningCircle.svg";
import AbnormalCircleIcon from "../../assets/images/AbnormalCircle.svg";
import ListIcon from "../../assets/images/ListIcon.svg";
import ResultCalendarMonth from "../../assets/images/ResultCalendarMonth.svg";
import EditIcon from "../../assets/images/EditIcon.svg";
import EditDone from "../../assets/images/EditDone.svg";
import EditCancel from "../../assets/images/EditCancel.svg";
//API
import { PatientApi, DiagnosisApi } from "../../api";
//CSS
import "./Result.css";
import Footer from "../Footer/Footer";
import SecureStorage from "react-secure-storage";
// 진단 결과 아이템 컴포넌트
const DiagnosisResultItem = ({
  position,
  diagnosisData,
  result,
  setResult,
  tempResult,
  setTempResult,
  formatSurveyResult,
  formatDiagnosisWithLineBreaks,
  onEditModeChange,
}) => {
  const [editMode, setEditMode] = useState(false);

  // 수정 모드 변경 시 부모에 알림 (저장 버튼 비활성화용)
  useEffect(() => {
    onEditModeChange?.(editMode);
  }, [editMode, onEditModeChange]);
  const [diagnosis, setDiagnosis] = useState(
    diagnosisData?.diagnosisList?.[0]?.finalDiagnosis || ""
  );
  const [aiDiagnosis, setAiDiagnosis] = useState(
    diagnosisData?.diagnosisList?.[0]?.aiDiagnosis || ""
  );
  const textareaRef = useRef(null);
  const aiDiagnosisTextareaRef = useRef(null);

  // diagnosisData 변경 시 초기값 설정
  useEffect(() => {
    if (diagnosisData?.diagnosisList?.[0]?.finalDiagnosis) {
      setDiagnosis(diagnosisData.diagnosisList[0].finalDiagnosis);
    }
    if (diagnosisData?.diagnosisList?.[0]?.aiDiagnosis) {
      setAiDiagnosis(diagnosisData.diagnosisList[0].aiDiagnosis);
    }
  }, [
    diagnosisData?.diagnosisList?.[0]?.finalDiagnosis,
    diagnosisData?.diagnosisList?.[0]?.aiDiagnosis,
  ]);

  const adjustFinalDiagnosisHeight = useCallback(() => {
    if (!textareaRef?.current) return;
    const textarea = textareaRef.current;
    const previousHeight = textarea.style.height;
    textarea.style.height = "auto";
    const scrollHeight = textarea.scrollHeight;
    const newHeight = Math.max(100, scrollHeight);
    if (previousHeight !== `${newHeight}px`) {
      textarea.style.height = `${newHeight}px`;
    }
  }, []);

  // aiDiagnosis textarea 높이 자동 조절
  const adjustAiDiagnosisHeight = useCallback(() => {
    if (!aiDiagnosisTextareaRef?.current) return;
    const textarea = aiDiagnosisTextareaRef.current;
    const previousHeight = textarea.style.height;
    textarea.style.height = "auto";
    const scrollHeight = textarea.scrollHeight;
    const newHeight = Math.min(500, Math.max(80, scrollHeight));
    if (previousHeight !== `${newHeight}px`) {
      textarea.style.height = `${newHeight}px`;
    }
  }, []);

  useEffect(() => {
    requestAnimationFrame(() => {
      adjustFinalDiagnosisHeight();
    });
  }, [diagnosis, adjustFinalDiagnosisHeight]);

  useEffect(() => {
    requestAnimationFrame(() => {
      adjustAiDiagnosisHeight();
    });
  }, [aiDiagnosis, adjustAiDiagnosisHeight]);

  // result 업데이트 헬퍼 함수 (editMode가 false일 때)
  const updateResult = useCallback(
    (field, value) => {
      if (editMode || !setResult || !result) return;

      const pos = position.toLowerCase();
      setResult({
        ...result,
        [pos]: {
          ...result[pos],
          diagnosisList: [
            {
              ...result[pos].diagnosisList[0],
              [field]: value,
            },
            ...result[pos].diagnosisList.slice(1),
          ],
        },
      });
    },
    [editMode, result, position, setResult]
  );

  // tempResult 업데이트 헬퍼 함수 (editMode가 true일 때)
  const updateTempResult = useCallback(
    (field, value) => {
      if (!editMode || !setTempResult || !tempResult) return;

      const pos = position.toLowerCase();
      setTempResult({
        ...tempResult,
        [pos]: {
          ...tempResult[pos],
          diagnosisList: [
            {
              ...tempResult[pos].diagnosisList[0],
              [field]: value,
            },
            ...tempResult[pos].diagnosisList.slice(1),
          ],
        },
      });
    },
    [editMode, tempResult, position, setTempResult]
  );

  const handleDiagnosisChange = useCallback(
    (e) => {
      const textarea = e.target;
      const cursorPosition = textarea.selectionStart;
      const newValue = e.target.value;

      // 편집 시 입력값 그대로 저장 (엔터 = 개행 유지)
      setDiagnosis(newValue);
      if (editMode) {
        updateTempResult("finalDiagnosis", newValue);
      } else {
        updateResult("finalDiagnosis", newValue);
      }
      requestAnimationFrame(() => {
        adjustFinalDiagnosisHeight();
        textarea.setSelectionRange(cursorPosition, cursorPosition);
      });
    },
    [editMode, updateTempResult, updateResult, adjustFinalDiagnosisHeight]
  );

  const handleAiDiagnosisChange = useCallback(
    (e) => {
      const textarea = e.target;
      const cursorPosition = textarea.selectionStart;
      const newValue = e.target.value;

      // 편집 시 입력값 그대로 저장 (엔터 = 개행 유지)
      setAiDiagnosis(newValue);
      if (editMode) {
        updateTempResult("aiDiagnosis", newValue);
      } else {
        updateResult("aiDiagnosis", newValue);
      }
      requestAnimationFrame(() => {
        adjustAiDiagnosisHeight();
        textarea.setSelectionRange(cursorPosition, cursorPosition);
      });
    },
    [editMode, updateTempResult, updateResult, adjustAiDiagnosisHeight]
  );

  const handleEditToggle = useCallback(() => {
    if (editMode) {
      // 저장하기: tempResult를 result에 반영
      if (setResult && tempResult) {
        setResult(JSON.parse(JSON.stringify(tempResult)));
      }
      setEditMode(false);
    } else {
      // 수정하기: result를 tempResult에 복사하고, tempResult의 값으로 textarea 덮어쓰기
      if (setTempResult && result) {
        const copiedResult = JSON.parse(JSON.stringify(result));
        setTempResult(copiedResult);

        // tempResult의 값으로 textarea 덮어쓰기
        const tempData = copiedResult[position.toLowerCase()];
        if (tempData?.diagnosisList?.[0]?.finalDiagnosis) {
          setDiagnosis(tempData.diagnosisList[0].finalDiagnosis);
        }
        if (tempData?.diagnosisList?.[0]?.aiDiagnosis) {
          setAiDiagnosis(tempData.diagnosisList[0].aiDiagnosis);
        }
      }
      setEditMode(true);
    }
  }, [editMode, tempResult, result, position, setResult, setTempResult]);

  const getStatusText = useCallback((status) => {
    const statusMap = {
      GREEN: "정상",
      YELLOW: "주의",
      RED: "위험",
    };
    return statusMap[status] || "위험";
  }, []);

  const getStatusIcon = useCallback((status) => {
    if (status === "GREEN") return <CheckCircleIcon />;
    if (status === "YELLOW") return <WarningCircleIcon />;
    return <AbnormalCircleIcon />;
  }, []);

  // 상태 변경 핸들러
  const handleStatusChange = useCallback(
    (statusType, newStatus) => {
      if (!editMode || !setTempResult || !tempResult) return;

      const pos = position.toLowerCase();
      const updatedTempResult = JSON.parse(JSON.stringify(tempResult));

      if (updatedTempResult[pos]?.diagnosisList?.[0]) {
        updatedTempResult[pos].diagnosisList[0][statusType] = newStatus;
        setTempResult(updatedTempResult);
      }
    },
    [editMode, tempResult, position, setTempResult]
  );

  // editMode일 때는 tempResult에서, 아닐 때는 diagnosisData에서 가져오기
  const currentDiagnosisItem = editMode
    ? tempResult?.[position.toLowerCase()]?.diagnosisList?.[0]
    : diagnosisData?.diagnosisList?.[0];

  const diagnosisItem = currentDiagnosisItem || diagnosisData?.diagnosisList?.[0];
  if (!diagnosisItem) return null;

  return (
    <div className="result-header-container-item">
      <div
        className={`result-header-top result-header-top-${diagnosisItem.finalDiagnosisStatus}`}
      >
        <div className="result-header-top-title">
          {position === "LEFT" ? "Left(좌측)" : "Right(우측)"}
        </div>
        <div className="result-header-top-edit-button-container">
          {editMode && (
            <div
              className="result-header-top-edit-button-cancel"
              onClick={() => {
                // 취소: result를 tempResult에 복원하고, result의 값으로 textarea 덮어쓰기
                if (setTempResult && result) {
                  const copiedResult = JSON.parse(JSON.stringify(result));
                  setTempResult(copiedResult);

                  // result의 값으로 textarea 덮어쓰기
                  const resultData = result[position.toLowerCase()];
                  if (resultData?.diagnosisList?.[0]?.finalDiagnosis) {
                    setDiagnosis(resultData.diagnosisList[0].finalDiagnosis);
                  }
                  if (resultData?.diagnosisList?.[0]?.aiDiagnosis) {
                    setAiDiagnosis(resultData.diagnosisList[0].aiDiagnosis);
                  }
                }
                setEditMode(false);
              }}
            >
              <EditCancel />
              취소
            </div>
          )}
          <div
            className="result-header-top-edit-button"
            onClick={handleEditToggle}
          >
            {editMode ? <EditDone /> : <EditIcon />}
            <span className="result-header-top-edit-button-text">
              {editMode ? "저장" : "수정"}
            </span>
          </div>
        </div>
      </div>
      <div className="result-header-top-content-container">
        <div className="result-header-top-content">
          <div className="result-header-top-content-item">
            <div className="result-header-top-content-item-label">
              종합 소견
            </div>
            {editMode ? (
              <div className="status-selector">
                {["GREEN", "YELLOW", "RED"].map((status) => (
                  <div
                    key={status}
                    className={`type type-${status} ${
                      diagnosisItem.finalDiagnosisStatus === status
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => handleStatusChange("finalDiagnosisStatus", status)}
                    style={{ cursor: "pointer" }}
                  >
                    {getStatusIcon(status)}
                    {getStatusText(status)}
                  </div>
                ))}
              </div>
            ) : (
              <div className={`type type-${diagnosisItem.finalDiagnosisStatus}`}>
                {getStatusIcon(diagnosisItem.finalDiagnosisStatus)}
                {getStatusText(diagnosisItem.finalDiagnosisStatus)}
              </div>
            )}
          </div>
          <img
            className="result-header-top-content-item-image"
            src={diagnosisItem.woundPictureUrl}
            alt={`${position.toLowerCase()}-image`}
          />
          <textarea
            ref={textareaRef}
            className="result-header-top-content-item-result"
            value={editMode ? diagnosis : formatDiagnosisWithLineBreaks(diagnosis)}
            onChange={handleDiagnosisChange}
            readOnly={!editMode}
          />
        </div>
        <div className="result-header-top-content">
          <div className="result-header-top-content-item">
            <div className="result-header-top-content-item-label">
              초음파 스크리닝
            </div>
            {editMode ? (
              <div className="status-selector">
                {["GREEN", "YELLOW", "RED"].map((status) => (
                  <div
                    key={status}
                    className={`type type-${status} ${
                      diagnosisItem.aiDiagnosisStatus === status
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => handleStatusChange("aiDiagnosisStatus", status)}
                    style={{ cursor: "pointer" }}
                  >
                    {getStatusIcon(status)}
                    {getStatusText(status)}
                  </div>
                ))}
              </div>
            ) : (
              <div className={`type type-${diagnosisItem.aiDiagnosisStatus}`}>
                {getStatusIcon(diagnosisItem.aiDiagnosisStatus)}
                {getStatusText(diagnosisItem.aiDiagnosisStatus)}
              </div>
            )}
          </div>
          <textarea
            ref={aiDiagnosisTextareaRef}
            className="result-header-top-content-item-result"
            value={editMode ? aiDiagnosis : formatDiagnosisWithLineBreaks(aiDiagnosis)}
            onChange={handleAiDiagnosisChange}
            readOnly={!editMode}
          />
        </div>
        <div className="result-header-top-content">
          <div className="result-header-top-content-item">
            <div className="result-header-top-content-item-label">
              문진 결과
            </div>
            {editMode ? (
              <div className="status-selector">
                {["GREEN", "YELLOW", "RED"].map((status) => (
                  <div
                    key={status}
                    className={`type type-${status} ${
                      diagnosisItem.surveyStatus === status ? "selected" : ""
                    }`}
                    onClick={() => handleStatusChange("surveyStatus", status)}
                    style={{ cursor: "pointer" }}
                  >
                    {getStatusIcon(status)}
                    {getStatusText(status)}
                  </div>
                ))}
              </div>
            ) : (
              <div className={`type type-${diagnosisItem.surveyStatus}`}>
                {getStatusIcon(diagnosisItem.surveyStatus)}
                {getStatusText(diagnosisItem.surveyStatus)}
              </div>
            )}
          </div>
          <div className="result-header-top-content-item-result-survey">
            {formatSurveyResult(diagnosisItem.survey)?.map((item, index) => (
              <div
                key={index}
                className="result-header-top-content-item-result-survey-item"
              >
                <div className="result-header-top-content-item-result-survey-item-question">
                  {item.question}
                </div>
                <div className="result-header-top-content-item-result-survey-item-answer">
                  {Array.isArray(item.answer)
                    ? item.answer.map((answer, idx) => (
                        <div key={idx}>
                          {answer}
                          {idx < item.answer.length - 1 && <br />}
                        </div>
                      ))
                    : item.answer}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const Result = ({ result, setResult, tempResult, setTempResult }) => {
  const navigate = useNavigate();
  const [isLeftEditMode, setIsLeftEditMode] = useState(false);
  const [isRightEditMode, setIsRightEditMode] = useState(false);
  const isAnyInEditMode = isLeftEditMode || isRightEditMode;

  // tempResult 초기화
  useEffect(() => {
    if (result && setTempResult) {
      setTempResult(JSON.parse(JSON.stringify(result)));
    }
  }, [result, setTempResult]);

  // result를 API 호출 형식으로 변환하는 함수
  const formatResultForApi = useCallback((resultData) => {
    if (!resultData) return null;

    const apiData = {
      patientId:
        resultData.patientId || resultData.left?.diagnosisList?.[0]?.diagnosisId
          ? resultData.left.diagnosisList[0].diagnosisId
          : null,
    };

    // left 데이터 변환
    if (resultData.left?.diagnosisList?.[0]) {
      const leftDiagnosis = resultData.left.diagnosisList[0];
      apiData.left = {
        diagnosisId: leftDiagnosis.diagnosisId,
        finalDiagnosis: leftDiagnosis.finalDiagnosis,
        aiDiagnosis: leftDiagnosis.aiDiagnosis,
      };
    }

    // right 데이터 변환
    if (resultData.right?.diagnosisList?.[0]) {
      const rightDiagnosis = resultData.right.diagnosisList[0];
      apiData.right = {
        diagnosisId: rightDiagnosis.diagnosisId,
        finalDiagnosis: rightDiagnosis.finalDiagnosis,
        aiDiagnosis: rightDiagnosis.aiDiagnosis,
      };
    }

    return apiData;
  }, []);

  const handleSave = async () => {
    try {
      if (!result) {
        console.error("result가 없습니다.");
        return;
      }
      const doctorId = SecureStorage.getItem("doctorId");
      const patientCode = SecureStorage.getItem("createdPatientCode");
      const surgeryId = result.left?.surgeryId || result.right?.surgeryId;

      // diagnosisDtoList 배열 생성
      const diagnosisDtoList = [];

      if (result.left?.diagnosisList?.[0]) {
        const leftDiagnosis = result.left.diagnosisList[0];
        diagnosisDtoList.push({
          diagnosisId: leftDiagnosis.diagnosisId,
          survey: leftDiagnosis.survey || "",
          surveyStatus: leftDiagnosis.surveyStatus,
          aiDiagnosis: leftDiagnosis.aiDiagnosis || "",
          aiDiagnosisStatus: leftDiagnosis.aiDiagnosisStatus,
          woundPictureUrl: leftDiagnosis.woundPictureUrl || "",
          finalDiagnosis: leftDiagnosis.finalDiagnosis || "",
          finalDiagnosisStatus: leftDiagnosis.finalDiagnosisStatus,
          position: "LEFT",
        });
      }

      if (result.right?.diagnosisList?.[0]) {
        const rightDiagnosis = result.right.diagnosisList[0];
        diagnosisDtoList.push({
          diagnosisId: rightDiagnosis.diagnosisId,
          survey: rightDiagnosis.survey || "",
          surveyStatus: rightDiagnosis.surveyStatus,
          aiDiagnosis: rightDiagnosis.aiDiagnosis || "",
          aiDiagnosisStatus: rightDiagnosis.aiDiagnosisStatus,
          woundPictureUrl: rightDiagnosis.woundPictureUrl || "",
          finalDiagnosis: rightDiagnosis.finalDiagnosis || "",
          finalDiagnosisStatus: rightDiagnosis.finalDiagnosisStatus,
          position: "RIGHT",
        });
      }

      const apiData = {
        doctorId: doctorId,
        patientCode: patientCode,
        surgeryUpdate: {
          surgeryId: surgeryId,
          diagnosisDtoList: diagnosisDtoList,
        },
      };

      const response = await PatientApi.registerPatient(apiData);
      if (response.statusCode === 200) {
        navigate(`/result/${response.data.patientId}`);
      } else {
        alert("저장에 실패했습니다.");
      }
      // 성공 시 알림
      // alert("저장되었습니다.");
    } catch (error) {
      console.error("save error", error);
      alert("저장에 실패했습니다.");
    }
  };

  const formatSurveyResult = useCallback((surveyString) => {
    if (!surveyString) return null;

    const answers = surveyString.split(",");
    if (answers.length !== 3) return null;

    const answerMaps = {
      question1: {
        0: "응답 없음",
        1: "전혀 아프지 않다.",
        2: "뻐근한 정도 통증이 있다.",
        3: "일상생활에 지장을 받을 정도 통증이 있다.",
        4: "잠을 못잘 정도 통증이 있다.",
      },
      question2: {
        0: "응답 없음",
        1: "어깨",
        2: "팔뚝",
        3: "목 · 승모근 · 견갑골 안쪽의 아픔, 손 · 팔의 저림",
      },
      question3: {
        0: "응답 없음",
        1: "가만히 있어도 아프다.",
        2: "특정 자세나 동작을 할 때만 아프다.",
        3: "어깨를 움직일 때 아프다.",
        4: "목을 움직일 때 아프다.",
      },
    };

    const getAnswers = (answerString, answerMap) => {
      if (!answerString) return ["응답 없음"];
      const numbers = answerString.trim().split(/\s+/).filter(Boolean);
      const answers = numbers
        .map((num) => answerMap[num.trim()] || "응답 없음")
        .filter(Boolean);
      return answers.length > 0 ? answers : ["응답 없음"];
    };

    return [
      {
        question: "통증 정도",
        answer: getAnswers(answers[0], answerMaps.question1),
      },
      {
        question: "통증 부위",
        answer: getAnswers(answers[1], answerMaps.question2),
      },
      {
        question: "통증 유발 동작",
        answer: getAnswers(answers[2], answerMaps.question3),
      },
    ];
  }, []);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    let hours = date.getHours();
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours || 12;
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    return `${year}-${month}-${day} ${ampm} ${hours}:${minutes}:${seconds}`;
  }, []);

  const formatDiagnosisWithLineBreaks = useCallback((text) => {
    if (!text) return "";
    return text.replace(/\.\s+/g, ".\n");
  }, []);

  const goToMain = useCallback(() => {
    navigate("/home");
  }, [navigate]);

  return (
    <>
      <div className="result-header">
        <div className="result-header-left">
          <ListIcon alt="list-icon" className="result-header-icon" />
          <div className="result-header-title">AI 진단 보조 결과 리포트</div>
        </div>
        <div className="result-header-date">
          <ResultCalendarMonth />
          {result?.left?.insertDate ? formatDate(result.left.insertDate) : ""}
        </div>
      </div>
      <div className="result-header-container">
        {result.left && (
          <DiagnosisResultItem
            position="LEFT"
            diagnosisData={result.left}
            result={result}
            setResult={setResult}
            tempResult={tempResult}
            setTempResult={setTempResult}
            formatSurveyResult={formatSurveyResult}
            formatDiagnosisWithLineBreaks={formatDiagnosisWithLineBreaks}
            onEditModeChange={setIsLeftEditMode}
          />
        )}
        {result.right && (
          <DiagnosisResultItem
            position="RIGHT"
            diagnosisData={result.right}
            result={result}
            setResult={setResult}
            tempResult={tempResult}
            setTempResult={setTempResult}
            formatSurveyResult={formatSurveyResult}
            formatDiagnosisWithLineBreaks={formatDiagnosisWithLineBreaks}
            onEditModeChange={setIsRightEditMode}
          />
        )}
      </div>
      <div style={{ width: "100%", padding: "0px 40px", marginTop: "30px" }}>
        <Footer />
      </div>
      <div className="result-button-container">
        <div className="result-back-button" onClick={goToMain}>
          홈으로
        </div>
        <div
          className={`result-save-button ${isAnyInEditMode ? "result-save-button-disabled" : ""}`}
          onClick={isAnyInEditMode ? undefined : handleSave}
          role="button"
          aria-disabled={isAnyInEditMode}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="35"
            height="35"
            viewBox="0 0 35 35"
            fill="none"
          >
            <mask
              id="mask0_701_579"
              style={{ maskType: "alpha" }}
              maskUnits="userSpaceOnUse"
              x="0"
              y="0"
              width="35"
              height="35"
            >
              <rect width="35" height="35" fill="#D9D9D9" />
            </mask>
            <g mask="url(#mask0_701_579)">
              <path
                d="M5.83301 26.25V8.75V16.9418V16.2659V26.25ZM6.28181 28.4375C5.54511 28.4375 4.92155 28.1823 4.41113 27.6719C3.90072 27.1615 3.64551 26.5379 3.64551 25.8012V9.1988C3.64551 8.4621 3.90072 7.83854 4.41113 7.32813C4.92155 6.81771 5.54511 6.5625 6.28181 6.5625H13.1976C13.549 6.5625 13.887 6.6308 14.2115 6.7674C14.5357 6.90375 14.8176 7.09163 15.057 7.33104L17.2051 9.47917H28.7175C29.4542 9.47917 30.0778 9.73438 30.5882 10.2448C31.0986 10.7552 31.3538 11.3788 31.3538 12.1155V16.3866C31.3538 16.6969 31.2491 16.9569 31.0396 17.1664C30.8303 17.3757 30.5705 17.4803 30.2601 17.4803C29.9497 17.4803 29.6899 17.3757 29.4806 17.1664C29.2711 16.9569 29.1663 16.6969 29.1663 16.3866V12.1155C29.1663 11.9845 29.1243 11.8769 29.0402 11.7928C28.9561 11.7087 28.8485 11.6667 28.7175 11.6667H17.4016C17.0501 11.6667 16.7122 11.5984 16.3877 11.4618C16.0632 11.3254 15.7814 11.1375 15.5422 10.8981L13.5202 8.87615C13.4733 8.82948 13.4242 8.79679 13.373 8.77807C13.3214 8.75936 13.2676 8.75 13.2114 8.75H6.28181C6.1508 8.75 6.04325 8.79205 5.95915 8.87615C5.87506 8.96024 5.83301 9.0678 5.83301 9.1988V25.8012C5.83301 25.9322 5.87506 26.0398 5.95915 26.1239C6.04325 26.208 6.1508 26.25 6.28181 26.25H15.054C15.3644 26.25 15.6244 26.3548 15.8339 26.5643C16.0432 26.7735 16.1478 27.0334 16.1478 27.3438C16.1478 27.6541 16.0432 27.914 15.8339 28.1232C15.6244 28.3327 15.3644 28.4375 15.054 28.4375H6.28181ZM24.7017 26.6707L23.6527 25.6218C23.4807 25.4497 23.2792 25.3652 23.0483 25.3681C22.8174 25.3707 22.6113 25.4627 22.4299 25.6441C22.2486 25.8256 22.158 26.0294 22.158 26.2555C22.158 26.4818 22.2486 26.6856 22.4299 26.8669L23.7789 28.2158C24.0426 28.4795 24.3502 28.6114 24.7017 28.6114C25.0531 28.6114 25.3606 28.4795 25.6241 28.2158L29.0599 24.803C29.2317 24.6309 29.3201 24.4256 29.3249 24.1872C29.3295 23.9488 29.2412 23.7389 29.0599 23.5576C28.8783 23.3762 28.6745 23.2893 28.4485 23.2969C28.2222 23.3044 28.0184 23.3913 27.8371 23.5576L24.7017 26.6707ZM25.7447 19.4633C27.5659 19.4633 29.1149 20.1018 30.3917 21.3788C31.6687 22.6556 32.3072 24.2046 32.3072 26.0258C32.3072 27.8468 31.6687 29.3957 30.3917 30.6728C29.1149 31.9495 27.5659 32.5879 25.7447 32.5879C23.9238 32.5879 22.3748 31.9495 21.0977 30.6728C19.8207 29.3957 19.1822 27.8468 19.1822 26.0258C19.1822 24.2046 19.8207 22.6556 21.0977 21.3788C22.3748 20.1018 23.9238 19.4633 25.7447 19.4633Z"
                fill="white"
              />
            </g>
          </svg>
          저장하기
        </div>
      </div>
    </>
  );
};

export default Result;

